/**
 * The universal search — pure matching and ranking, no React and no PocketBase.
 *
 * The dashboard is the one screen that reads across every other surface, so it
 * is the natural home for a single field that finds a project, a Flows card, an
 * inbox item, a task or a board card and takes you straight to it. Everything that decides
 * *what matches* and *in what order* lives here so it can be reasoned about
 * without a running app.
 *
 * ## Ranking
 *
 * The owner fixed the order by kind, not by score: a matching **project** comes
 * before a matching **inbox** item, before a **task**, before a **card**. So the
 * primary sort key is {@link KIND_PRIORITY} and the fuzzy score only breaks ties
 * within a kind. Typing "control" surfaces the Control board first even when a
 * card's title matches more tightly — which is exactly what "show Control first"
 * asks for.
 */

import { TAB_LABEL, cardLink, tabOf, type FlowCard } from "@/features/flows/model";

export type SearchResultKind = "project" | "flow" | "inbox" | "task" | "card";

/** Kind order, lowest first. The primary sort key — see the module comment. */
export const KIND_PRIORITY: Record<SearchResultKind, number> = {
  project: 0,
  flow: 1,
  inbox: 2,
  task: 3,
  card: 4,
};

export type SearchResult = {
  kind: SearchResultKind;
  /** The record id (or slug, for a project) — enough to open or route to it. */
  id: string;
  /** Primary label shown in the result row. */
  title: string;
  /** The context line under it: a sender, a day, a project name. */
  subtitle?: string;
  /** Router target, e.g. `/kanban/control` or `/inbox?peek=abc123`. */
  to: string;
  /** Higher is a tighter match. Only ever compared within one kind. */
  score: number;
};

/* -------------------------------------------------------------------------- */
/* Fuzzy matching                                                              */
/* -------------------------------------------------------------------------- */

/** True at a word start: the first character, or one after a space/`-_/`. */
function isBoundary(text: string, index: number): boolean {
  if (index <= 0) {
    return true;
  }
  const before = text[index - 1] ?? "";
  return before === " " || before === "-" || before === "_" || before === "/";
}

/**
 * Score how well `query` matches `text`, or `null` when it does not at all.
 *
 * Two tiers, and a substring always beats a scattered subsequence:
 *
 * - **Substring** (`text` contains `query`): a high base, with bonuses for
 *   starting at the front, starting on a word boundary, and for the match
 *   filling more of the haystack. "auth" over "Authentication" scores well;
 *   over "de-authorise the beta" less so.
 * - **Subsequence** (the query's letters appear in order but apart): a much
 *   lower base, rewarding contiguous runs and word-boundary hits so "cb" finds
 *   "Content Builder" ahead of "crumbling".
 *
 * Case-insensitive. An empty query matches nothing — a blank field shows no
 * results rather than everything.
 */
export function fuzzyScore(text: string, query: string): number | null {
  const q = query.trim().toLowerCase();
  if (q === "") {
    return null;
  }
  const t = text.toLowerCase();

  const at = t.indexOf(q);
  if (at !== -1) {
    let score = 1000 - Math.min(at, 200);
    if (at === 0) {
      score += 400;
    } else if (isBoundary(t, at)) {
      score += 200;
    }
    // Tighter fit (less left over) ranks higher.
    score += Math.max(0, 120 - (t.length - q.length));
    return score;
  }

  let ti = 0;
  let qi = 0;
  let score = 0;
  let run = 0;
  let prev = -2;
  while (ti < t.length && qi < q.length) {
    if (t[ti] === q[qi]) {
      if (prev === ti - 1) {
        run += 1;
        score += 15 + run * 5;
      } else {
        run = 0;
        score += 5;
      }
      if (isBoundary(t, ti)) {
        score += 20;
      }
      prev = ti;
      qi += 1;
    }
    ti += 1;
  }
  if (qi < q.length) {
    return null;
  }
  // A long haystack that only barely contains the letters is a weak hit.
  return score - Math.floor(t.length / 20);
}

/** The best score across several haystacks (e.g. a card's title and its key). */
function bestScore(query: string, ...haystacks: (string | undefined)[]): number | null {
  let best: number | null = null;
  for (const hay of haystacks) {
    if (!hay) {
      continue;
    }
    const score = fuzzyScore(hay, query);
    if (score !== null && (best === null || score > best)) {
      best = score;
    }
  }
  return best;
}

/* -------------------------------------------------------------------------- */
/* Sources → results                                                           */
/* -------------------------------------------------------------------------- */

export type ProjectSource = { id: string; name: string; slug: string };
export type InboxSource = { id: string; title: string; sender: string };
export type FlowSource = { id: string; title: string; categoryName: string; status: FlowCard["status"] };
export type TaskSource = { id: string; title: string; dateKey: string; subtitle: string };
export type CardSource = {
  id: string;
  title: string;
  issueKey: string;
  projectSlug: string;
  projectName: string;
};

export type SearchSources = {
  projects: readonly ProjectSource[];
  flows: readonly FlowSource[];
  inbox: readonly InboxSource[];
  tasks: readonly TaskSource[];
  cards: readonly CardSource[];
};

/** Route a project search hit to its board — the slug is the path identity. */
function projectTo(slug: string): string {
  return `/kanban/${encodeURIComponent(slug)}`;
}

/**
 * Turn everything loaded into a ranked, deep-linkable result list.
 *
 * Each hit's `to` is the exact router target the search field navigates to:
 * a project opens its board; an inbox item, a task and a card each open their
 * surface with `?peek=<id>` so the item is already open on arrival (see
 * `usePeekParam`). A task also carries its own day in the path so the record
 * is on screen for the peek to find.
 *
 * Sorted by {@link KIND_PRIORITY} first and score second, then capped at
 * `limit` — the kind order is the owner's, not a heuristic.
 */
export function buildResults(
  sources: SearchSources,
  query: string,
  limit = 12,
): SearchResult[] {
  const q = query.trim();
  if (q === "") {
    return [];
  }

  const results: SearchResult[] = [];

  for (const project of sources.projects) {
    const score = fuzzyScore(project.name, q);
    if (score !== null) {
      results.push({
        kind: "project",
        id: project.slug,
        title: project.name,
        subtitle: "Project board",
        to: projectTo(project.slug),
        score,
      });
    }
  }

  for (const item of sources.inbox) {
    const score = bestScore(q, item.title, item.sender);
    if (score !== null) {
      results.push({
        kind: "inbox",
        id: item.id,
        title: item.title,
        subtitle: item.sender ? `From ${item.sender}` : "Inbox",
        to: `/inbox?peek=${encodeURIComponent(item.id)}`,
        score,
      });
    }
  }

  for (const flow of sources.flows) {
    const score = bestScore(q, flow.title, flow.categoryName);
    if (score !== null) {
      // Flows lists a card only on its own tab, so the link names the tab too.
      results.push({
        kind: "flow",
        id: flow.id,
        title: flow.title,
        subtitle: `${flow.categoryName} · ${TAB_LABEL[tabOf(flow)]}`,
        to: cardLink(flow),
        score,
      });
    }
  }

  for (const task of sources.tasks) {
    const score = fuzzyScore(task.title, q);
    if (score !== null) {
      results.push({
        kind: "task",
        id: task.id,
        title: task.title,
        subtitle: task.subtitle,
        to: `/tasks/${encodeURIComponent(task.dateKey)}?peek=${encodeURIComponent(task.id)}`,
        score,
      });
    }
  }

  for (const card of sources.cards) {
    // A card whose project has not resolved to a slug has no board to open —
    // skip it rather than route to `/kanban/`. The load gate makes this rare,
    // but a card can arrive before its project does.
    if (card.projectSlug === "") {
      continue;
    }
    const score = bestScore(q, card.title, card.issueKey);
    if (score !== null) {
      results.push({
        kind: "card",
        id: card.id,
        title: card.title,
        subtitle: `${card.issueKey} · ${card.projectName}`,
        to: `/kanban/${encodeURIComponent(card.projectSlug)}?peek=${encodeURIComponent(card.id)}`,
        score,
      });
    }
  }

  results.sort((a, b) => {
    const byKind = KIND_PRIORITY[a.kind] - KIND_PRIORITY[b.kind];
    return byKind !== 0 ? byKind : b.score - a.score;
  });

  return results.slice(0, limit);
}
